import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/generics/page-title';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { ExternalLink, CreditCard, Loader2, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { subscriptionViewModel } from '../../view-model/professional/subscription-view-model';
import subscriptionStore from '../../store/subscription-store';
import CurrentPlanCard from '../../components/subscription/current-plan-card';

const Billing: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const currentLang = i18n.language || 'fr';

  useEffect(() => {
    // Charger l'abonnement actuel
    subscriptionViewModel.initialize();
  }, []);

  const handleOpenBillingPortal = async () => {
    setIsLoadingPortal(true);
    await subscriptionViewModel.openBillingPortal();
    setIsLoadingPortal(false);
  };

  const handleCancelSubscription = async () => {
    setShowCancelConfirm(false);
    await subscriptionViewModel.cancelSubscription();
  };

  const handleReactivateSubscription = async () => {
    await subscriptionViewModel.reactivateSubscription();
  };

  const handleChangeSubscription = () => {
    navigate(`/${currentLang}/provider/subscription`);
  };

  return (
    <div className="h-full flex flex-col space-y-4 overflow-hidden">
      <PageTitle
        title={t('billing.title')}
        subtitle={t('billing.subtitle')}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 overflow-auto pb-4">
        {/* Carte Portail de facturation Stripe */}
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('billing.invoicesPayments')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('billing.invoicesDescription')}
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col justify-between">
            <div className="space-y-4 mb-6">
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {t('billing.stripePortalDescription')}
              </p>

              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>{t('billing.portalFeature1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>{t('billing.portalFeature2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>{t('billing.portalFeature3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>{t('billing.portalFeature4')}</span>
                </li>
              </ul>

            </div>

            {/* Toujours afficher le bouton du portail si l'utilisateur a (ou a eu) un abonnement */}
            {subscriptionStore.currentSubscription && (
              <Button
                onClick={handleOpenBillingPortal}
                disabled={isLoadingPortal}
                variant="ghost"
                className="w-full"
              >
                {isLoadingPortal ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('billing.loading')}
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('billing.openStripePortal')}
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Current Plan Card ou Carte "Choisissez un abonnement" */}
        {subscriptionStore.currentSubscription ? (
          <CurrentPlanCard
            subscription={subscriptionStore.currentSubscription}
            plan={subscriptionStore.getCurrentPlan}
            onCancel={() => setShowCancelConfirm(true)}
            onReactivate={handleReactivateSubscription}
            onChangeSubscription={handleChangeSubscription}
            isLoading={subscriptionStore.isLoading}
          />
        ) : (
          <Card className="flex flex-col">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-dark/30 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-primary dark:text-primary-light" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('billing.chooseSubscriptionTitle', 'Choisissez un abonnement')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('billing.chooseSubscriptionSubtitle', 'Démarrez dès maintenant')}
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 flex flex-col justify-between">
              <div className="space-y-4 mb-6">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t('billing.noSubscriptionDescription', 'Vous n\'avez pas encore d\'abonnement actif. Choisissez le plan qui correspond le mieux à vos besoins et commencez à profiter de toutes les fonctionnalités.')}
                </p>

                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-xs text-yellow-800 dark:text-yellow-300">
                    {t('billing.requiresSubscription', 'Un abonnement actif est requis pour accéder à toutes les fonctionnalités.')}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleChangeSubscription}
                className="w-full bg-primary hover:bg-primary-dark"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {t('billing.chooseSubscription', 'Choisir un abonnement')}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de confirmation d'annulation */}
      {showCancelConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={() => setShowCancelConfirm(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {t('subscription.cancelConfirmTitle')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('subscription.cancelConfirmMessage')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('subscription.cancelConfirmNo')}
                </button>
                <button
                  onClick={handleCancelSubscription}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  {t('subscription.cancelConfirmYes')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Note de sécurité */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {t('billing.securityTitle')}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t('billing.securityDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Billing;
